import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GitCommit, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Iniettate al build time dal CI/CD (vedi .env.production o variabili Docker)
// In dev locale rimangono "dev"; in produzione mostrano sha + timestamp del build.
const FRONTEND_SHA = process.env.REACT_APP_BUILD_SHA || 'dev';
const FRONTEND_TIME = process.env.REACT_APP_BUILD_TIME || '';

interface BuildInfo {
  service: string;
  sha: string;
  branch: string;
  built_at: string;
  from_env: boolean;
}

function fmtTime(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  } catch {
    return iso.slice(0, 16);
  }
}

/**
 * Versione di build mostrata in fondo alla sidebar admin.
 *
 * - **Frontend** → `REACT_APP_BUILD_SHA` / `REACT_APP_BUILD_TIME` iniettate al build.
 * - **Backend**  → fetched da `/api/admin/build-info` (env `BUILD_SHA`/`BUILD_TIME`).
 *
 * Se il backend non risponde o la versione frontend è "dev", evidenzia in
 * giallo per segnalare che siamo in un ambiente non taggato (dev locale).
 */
export function BuildVersionBadge() {
  const [backend, setBackend] = useState<BuildInfo | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get<BuildInfo>(`${API}/api/build-info`)
      .then(res => setBackend(res.data))
      .catch(() => setError(true));
  }, []);

  const feIsDev = FRONTEND_SHA === 'dev';
  const beIsDev = backend ? !backend.from_env : false;

  return (
    <div
      className="text-[10px] font-mono space-y-0.5 px-1"
      data-testid="admin-build-version"
      title="Versione applicativa in esecuzione"
    >
      <div className="flex items-center gap-1 text-muted-foreground">
        <GitCommit className="w-2.5 h-2.5" />
        <span className="font-semibold uppercase tracking-wide text-[9px]">Build</span>
      </div>
      {/* Frontend */}
      <div
        className={`flex items-center justify-between gap-1 ${feIsDev ? 'text-amber-600' : 'text-muted-foreground'}`}
        data-testid="build-version-frontend"
      >
        <span className="opacity-70">FE:</span>
        <span className="truncate">
          {FRONTEND_SHA}
          {FRONTEND_TIME && <span className="opacity-60"> · {fmtTime(FRONTEND_TIME)}</span>}
        </span>
      </div>
      {/* Backend */}
      <div
        className={`flex items-center justify-between gap-1 ${
          error ? 'text-red-600' : beIsDev ? 'text-amber-600' : 'text-muted-foreground'
        }`}
        data-testid="build-version-backend"
      >
        <span className="opacity-70">BE:</span>
        {error ? (
          <span className="inline-flex items-center gap-0.5">
            <AlertCircle className="w-2.5 h-2.5" /> offline
          </span>
        ) : backend ? (
          <span className="truncate">
            {backend.sha}
            {backend.built_at && <span className="opacity-60"> · {fmtTime(backend.built_at)}</span>}
          </span>
        ) : (
          <span className="opacity-50">...</span>
        )}
      </div>
    </div>
  );
}
